import { useState, useEffect } from "react";
import { Offcanvas, Row, Col, Form, Button, Badge } from "react-bootstrap";
import { FaFlag, FaRegFlag, FaRegCircle, FaRegCheckCircle, FaTimes } from "react-icons/fa";
import useFetchData from "./useFetchDatav2";
import { useToast } from "../Context/useToast";

const FollowUpModal = ({ sdId, eshId, supplierInvoiceNumber, externalInvoiceNumber, onFollowUpStatusChange, onCloseFollowUpModal }) => {
   const [show, setShow] = useState(true);
   const [hoveredFollowUpId, setHoveredFollowUpId] = useState(null);
   const [payloadFollowUps, setPayloadFollowUps] = useState([]);
   const [editingIndex, setEditingIndex] = useState(null);
   const [originalFollowUps, setOriginalFollowUps] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const [error, setError] = useState(null);
   const { triggerToast } = useToast();
   const { fetchData } = useFetchData();

   // Debug: Log invoice numbers
   useEffect(() => {
      console.log("FollowUpModal received invoice numbers:", { supplierInvoiceNumber, externalInvoiceNumber });
   }, [supplierInvoiceNumber, externalInvoiceNumber]);

   // Fetch existing follow-ups (if any)
   useEffect(() => {
      const getFollowups = async () => {
         if (!sdId && !eshId) return;
         setIsLoading(true);
         const url = "/getFollowup.sz";
         const method = "POST";
         const formData = new FormData();
         formData.append("_sd_id", sdId);
         formData.append("_esh_id", eshId);
         try {
            const { responseData, fetchLoading, fetchError } = await fetchData(url, method, formData, "form-data");
            if (responseData.st === 1) {
               setPayloadFollowUps(responseData?.payload);

               // Check if any follow-up is outstanding (status = 1)
               const hasFollowUp = responseData.payload.some((fu) => fu.fu_status === 1);
               onFollowUpStatusChange?.(hasFollowUp); // Pass to calling parent
            } else {
               setPayloadFollowUps([]);
               if (responseData.m_en) {
                  triggerToast("warning", responseData.m_en, false, "", false);
               } else {
                  triggerToast("error", "We seem to have an issue retrieving follow ups, please try again later.", true, "Issue", false, 0);
               }
            }
         } catch (error) {
            setPayloadFollowUps([]);
            triggerToast("error", "We seem to have an access your data, please try again later.", true, "Network Issue", false, 0);
         } finally {
            setIsLoading(false);
         }
      };
      getFollowups();
   }, [sdId, eshId]);

   const handleAddFollowUp = () => {
      if (payloadFollowUps.length < 2) {
         const newFollowUp = {
            fu_id: Date.now(), // Temporary ID
            fu_title: "",
            fu_note: "",
            fu_status: 0,
            saved: false,
         };
         const updatedFollowUps = [...payloadFollowUps, newFollowUp];
         setPayloadFollowUps(updatedFollowUps);
         setEditingIndex(updatedFollowUps.length - 1);
      }
   };

   const onClose = () => {
      setShow(false);
      //onFollowUpStatusChange?.(payloadFollowUps.some((fu) => fu.fu_status === 1));
      onCloseFollowUpModal?.();
   };

   const handleEditMode = (index) => {
      // Store original values before editing
      setOriginalFollowUps({ ...payloadFollowUps[index] });
      setEditingIndex(index);
   };

   const handleInputChange = (index, field, value) => {
      const updatedFollowUps = [...payloadFollowUps];
      updatedFollowUps[index][field] = value;
      setPayloadFollowUps(updatedFollowUps);
   };

   useEffect(() => {
      const hasActive = payloadFollowUps.some((fu) => fu.fu_status === 1);
      setTimeout(() => {
         onFollowUpStatusChange?.(hasActive);
      }, 0);
   }, [payloadFollowUps.map((fu) => fu.fu_status).join(",")]);

   const handleSaveFollowUp = async (index, isStatusUpdate = false) => {
      const savedFollowUp = payloadFollowUps[index];

      setIsLoading(true);
      let entityType = "";
      if (sdId && eshId) {
         entityType = "BOTH";
      } else if (sdId) {
         entityType = "SUPPLIER_STATEMENT_DETAILS";
      } else if (eshId) {
         entityType = "EXTERNAL_SOURCE_HEADER";
      } else {
         return;
      }

      const followUpData = {
         ...savedFollowUp,
         fu_entity_type: entityType,
      };

      const isUpdating = savedFollowUp.fu_id.length > 20 ? true : false; // if fu_id exists its an update (not inserted)
      const url = isUpdating ? (isStatusUpdate ? "/update_followup_status.sz" : "/update_followup.sz") : "/insert_followup.sz";
      const method = "POST";
      const formData = new FormData();
      formData.append("_title", savedFollowUp["fu_title"]);
      formData.append("_note", savedFollowUp["fu_note"]);
      formData.append("_sd_id", sdId);
      formData.append("_esh_id", eshId);
      formData.append("_entity_type", entityType);
      if (isUpdating) {
         formData.append("_id", savedFollowUp.fu_id);
         if (isStatusUpdate) {
            formData.append("_status", savedFollowUp.fu_status);
         }
      }

      try {
         const { responseData, fetchLoading, fetchError } = await fetchData(url, method, formData, "form-data");
         if (responseData.st === 1) {
            const updatedFollowUps = [...payloadFollowUps];
            updatedFollowUps[index] = {
               ...savedFollowUp,
               fu_id: responseData.payload.fu_id,
               fu_status: isUpdating ? savedFollowUp.fu_status : 1,
               saved: true,
            };
            setPayloadFollowUps(updatedFollowUps);
            setEditingIndex(null);

            // only updates after modal closes
            //if (savedFollowUp.fu_status === 1) {
            //   onFollowUpStatusChange?.(true);
            // }

            // Notify reconcile as soon as a follow-up is created or updated with active status
            const hasFollowUp = updatedFollowUps.some((fu) => fu.fu_status === 1);
            onFollowUpStatusChange?.(hasFollowUp);
         } else {
            const updatedFollowUps = [...payloadFollowUps];
            updatedFollowUps[index].error = "Error saving follow-up. Please try again.";
            setPayloadFollowUps(updatedFollowUps);
         }
      } catch (error) {
         triggerToast("error", "We are experiencing connectivity issues, please try again later.", true, "Network Issue", false, 0);
      } finally {
         setIsLoading(false);
      }
   };

   const handleCancelEdit = (index) => {
      // Restore original values
      const updatedFollowUps = [...payloadFollowUps];
      updatedFollowUps[index] = originalFollowUps;
      setPayloadFollowUps(updatedFollowUps);
      setEditingIndex(null); // Exit edit mode
   };

   // const handleRemoveFollowUp = (index) => {
   //    const updatedFollowUps = payloadFollowUps.filter((_, i) => i !== index);
   //    setPayloadFollowUps(updatedFollowUps);
   //    setEditingIndex(null);
   // };

   return (
      <>
         <Offcanvas
            style={{
               width: "300px",
               position: "fixed", // Keep fixed positioning
               top: 0,
               right: 0,
               height: "100vh",
               overflow: "hidden",
            }}
            onHide={onClose}
            show={show}
            scroll={true}
            backdrop={false}
            placement="end">
            <Offcanvas.Header
               className="border-bottom"
               closeButton
               style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1055,
               }}>
               <Row className="d-flex flex-column w-100">
                  <Col>
                     <div className="fs-5 position-relative d-flex flex-column align-items-start">
                        <button className="btn btn-outline-primary rounded-pill pt-1 pb-1 d-flex align-items-center" type="button" id="addFollowUp" disabled={payloadFollowUps.length >= 2} style={{ textDecoration: "none" }} onClick={handleAddFollowUp} aria-expanded="false">
                           <FaRegFlag size={18} />
                           <span className="ms-2"> Add a Follow-Up</span>
                        </button>
                     </div>
                  </Col>
                  {(supplierInvoiceNumber?.trim() || externalInvoiceNumber?.trim()) && (
                     <Col className="mt-3">
                        <div className="d-flex flex-wrap gap-2">
                           {supplierInvoiceNumber?.trim() && (
                              <Badge pill
                                 bg="info" 
                                 className="d-flex align-items-center gap-2 px-3 py-2"
                                 style={{ fontSize: "0.85rem", fontWeight: 500 }}
                              >
                                 <span>Supplier: {supplierInvoiceNumber}</span>
                              </Badge>
                           )}
                          
                        </div>
                     </Col>
                  )}
               </Row>
            </Offcanvas.Header>
            <Offcanvas.Body className="pt-1" style={{ maxHeight: "calc(100vh - 3.5rem)", overflowY: "auto" }}>
               {payloadFollowUps.length === 0 ? (
                  <div className="text-center text-muted mt-4">No Follow-Ups found</div>
               ) : (
                  payloadFollowUps.map((followUp, index) => (
                     <div key={followUp.fu_id || `followUp-${index}`} className="border-bottom mb-2 mt-4">
                        <Row
                           className="align-items-center"
                           style={{ cursor: "pointer" }}
                           onClick={() => {
                              if (followUp.fu_status !== 2) {
                                 handleEditMode(index);
                              }
                           }}>
                           {editingIndex !== index && (
                              <Col
                                 xs="auto"
                                 onMouseEnter={() => {
                                    if (followUp.fu_status !== 2) {
                                       setHoveredFollowUpId(followUp.fu_id || index);
                                    }
                                 }}
                                 onMouseLeave={() => {
                                    if (followUp.fu_status !== 2) {
                                       setHoveredFollowUpId(null);
                                    }
                                 }}
                                 onClick={() => {
                                    if (followUp.fu_status === 1) {
                                       const updatedFollowUps = [...payloadFollowUps];
                                       updatedFollowUps[index].fu_status = 2;
                                       setPayloadFollowUps(updatedFollowUps);
                                       handleSaveFollowUp(index, true);
                                    }
                                 }}
                                 style={{ cursor: followUp.fu_status === 1 ? "pointer" : "default" }}>
                                 {followUp.fu_status === 1 ? hoveredFollowUpId === (followUp.fu_id || index) ? <FaRegCheckCircle size={20} color="green" /> : <FaRegCircle size={20} /> : followUp.fu_status === 2 ? <FaRegCheckCircle color="green" size={20} /> : null}
                              </Col>
                           )}
                           <Col>
                              {editingIndex === index ? (
                                 <Form.Control type="text" placeholder="Follow-Up Title" value={followUp.fu_title ?? ""} onChange={(e) => handleInputChange(index, "fu_title", e.target.value)} />
                              ) : (
                                 <div
                                    className="fw-bold ps-0"
                                    style={{ cursor: followUp.fu_status === 1 ? "pointer" : "default" }}
                                    onClick={() => {
                                       if (followUp.fu_status === 1) {
                                          handleEditMode(index);
                                       }
                                    }}>
                                    {followUp.fu_title}
                                 </div>
                              )}
                           </Col>
                        </Row>
                        <Row className="mt-2 mb-3">
                           <Col>
                              {editingIndex === index ? (
                                 <Form.Control as="textarea" rows={2} placeholder="Enter follow-up details" value={followUp.fu_note ?? ""} onChange={(e) => handleInputChange(index, "fu_note", e.target.value)} />
                              ) : (
                                 <div
                                    className="text-muted"
                                    style={{ cursor: followUp.fu_status === 1 ? "pointer" : "default" }}
                                    onClick={() => {
                                       if (followUp.fu_status === 1) {
                                          handleEditMode(index);
                                       }
                                    }}>
                                    {followUp.fu_note}
                                 </div>
                              )}
                           </Col>
                        </Row>

                        {editingIndex === index && (
                           <Row className="mt-2 mb-3">
                              <Col>
                                 <div className="d-flex justify-content-between mt-2">
                                    <button
                                       type="button"
                                       className="btn btn-outline-secondary rounded-pill pt-0 pb-0"
                                       style={{ textDecoration: "none" }}
                                       aria-expanded="false"
                                       onClick={() => handleCancelEdit(index)} // Call cancel function
                                    >
                                       <span className="fs-13">Cancel</span>
                                    </button>
                                    <button type="button" className="btn btn-outline-primary rounded-pill pt-0 pb-0" style={{ textDecoration: "none" }} aria-expanded="false" onClick={() => handleSaveFollowUp(index)}>
                                       <span className="fs-13">Save</span>
                                    </button>
                                 </div>
                              </Col>
                           </Row>
                        )}
                     </div>
                  ))
               )}
            </Offcanvas.Body>
            <div className="border-top p-3 position-sticky bottom-0 w-100" style={{ zIndex: 1055 }}>
               <Row>
                  <Col className="d-flex justify-content-between align-items-center"></Col>
               </Row>
            </div>
         </Offcanvas>
      </>
   );
};

export default FollowUpModal;
